const SEED_STORAGE_SYNC_EVENT = 'flux-seed-storage-sync';

interface SeedStorageGuard {
  storageKey: string;
  expectedValue: unknown;
  allowMissing?: boolean;
}

interface PersistedSeedAdvanceOptions {
  storageKey: string;
  fixedStorageKey: string;
  expectedSeed: number;
  nextSeed: number;
  guards?: SeedStorageGuard[];
}

interface SeedStorageSyncDetail {
  storageKey: string;
  value: unknown;
}

export type PersistedSeedAdvanceResult = 'advanced' | 'stale' | 'unavailable';

function readStoredJson(
  storageKey: string,
  storage: Storage = localStorage
): { found: boolean; value?: unknown } {
  const stored = storage.getItem(storageKey);
  return stored === null
    ? { found: false }
    : { found: true, value: JSON.parse(stored) };
}

/**
 * Advances a persisted seed only if no replacement renderer has changed its
 * seed, fixed flag, or controlling value since dispatch payload capture.
 */
export function persistSeedAdvance({
  storageKey,
  fixedStorageKey,
  expectedSeed,
  nextSeed,
  guards = [],
}: PersistedSeedAdvanceOptions): PersistedSeedAdvanceResult {
  if (typeof window === 'undefined') return 'unavailable';

  try {
    // The fixed flag is deliberately tab-scoped (sessionStorage): it must not
    // survive a browser restart or leak into other tabs.
    if (readStoredJson(fixedStorageKey, sessionStorage).value === true) return 'stale';

    const persistedSeed = readStoredJson(storageKey);
    if (!persistedSeed.found) return 'unavailable';
    if (!Object.is(persistedSeed.value, expectedSeed)) return 'stale';

    for (const guard of guards) {
      const persistedGuard = readStoredJson(guard.storageKey);
      if (
        (!persistedGuard.found && !guard.allowMissing)
        || (persistedGuard.found && !Object.is(persistedGuard.value, guard.expectedValue))
      ) {
        return 'stale';
      }
    }

    localStorage.setItem(storageKey, JSON.stringify(nextSeed));
    window.dispatchEvent(new CustomEvent<SeedStorageSyncDetail>(SEED_STORAGE_SYNC_EVENT, {
      detail: { storageKey, value: nextSeed },
    }));
    return 'advanced';
  } catch {
    return 'unavailable';
  }
}

/** Keeps a mounted replacement renderer synchronized with delayed advances. */
export function subscribeToSeedStorageSync(
  listener: (detail: SeedStorageSyncDetail) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const handleCustomEvent = (event: Event) => {
    const detail = (event as CustomEvent<SeedStorageSyncDetail>).detail;
    if (detail && typeof detail.storageKey === 'string') listener(detail);
  };
  const handleStorageEvent = (event: StorageEvent) => {
    if (!event.key || (event.storageArea && event.storageArea !== localStorage)) return;

    try {
      // Native storage events can be delivered after a newer write has already
      // landed. Treat the event as invalidation and read the current value so a
      // queued stale event cannot roll mounted renderer state backward.
      const currentValue = localStorage.getItem(event.key);
      listener({
        storageKey: event.key,
        value: currentValue === null ? undefined : JSON.parse(currentValue),
      });
    } catch {
      // Ignore malformed values written outside the application.
    }
  };

  window.addEventListener(SEED_STORAGE_SYNC_EVENT, handleCustomEvent);
  window.addEventListener('storage', handleStorageEvent);
  return () => {
    window.removeEventListener(SEED_STORAGE_SYNC_EVENT, handleCustomEvent);
    window.removeEventListener('storage', handleStorageEvent);
  };
}
